import { useMemo } from "react";
import type { PatientAction, PatientActionCheckIn } from "@ferrivia/api-contract";
import { useTranslation } from "@ferrivia/i18n";
import { Button } from "@ferrivia/ui";
import { KIND_ICON, useActionLabels } from "../../actions/kinds";
import { currentCheckIn, isActive, versionChain } from "../../actions/plan";
import { ActionDrawerShell } from "../ActionDrawer";
import s from "./ActionDetail.module.css";

export interface ActionDetailProps {
  container: HTMLElement | null;
  action: PatientAction;
  actions: PatientAction[];
  checkIns: PatientActionCheckIn[];
  clientName: string;
  onClose: () => void;
  onChange: () => void;
  onCheckIn: () => void;
  onOpenMessage: (messageId: string) => void;
}

type HistoryItem =
  | { type: "version"; at: string; action: PatientAction; previous: PatientAction | null }
  | { type: "checkIn"; at: string; checkIn: PatientActionCheckIn; action: PatientAction };

export function ActionDetail({
  container,
  action,
  actions,
  checkIns,
  clientName,
  onClose,
  onChange,
  onCheckIn,
  onOpenMessage,
}: ActionDetailProps) {
  const { t } = useTranslation("clinic");
  const { kindLabel, channelLabel, titleLabel, formatDay, formatTimestampDay, runsLabel } = useActionLabels();
  const Icon = KIND_ICON[action.kind];
  const active = isActive(action, actions, checkIns);
  const due = currentCheckIn(action, checkIns);
  const previous = action.replacesId ? (actions.find((a) => a.id === action.replacesId) ?? null) : null;

  const history = useMemo(() => {
    const chain = versionChain(action, actions);
    const byId = new Map(chain.map((a) => [a.id, a]));
    const items: HistoryItem[] = [
      ...chain.map((a): HistoryItem => ({
        type: "version",
        at: a.createdAt,
        action: a,
        previous: a.replacesId ? (byId.get(a.replacesId) ?? null) : null,
      })),
      ...checkIns
        .filter((c) => byId.has(c.actionId))
        .map((c): HistoryItem => ({ type: "checkIn", at: c.createdAt, checkIn: c, action: byId.get(c.actionId)! })),
    ];
    return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [action, actions, checkIns]);

  function outcomeLabel(outcome: PatientActionCheckIn["outcome"]): string {
    switch (outcome) {
      case "continue":
        return t("actionDetail.outcome.continue", "Continued");
      case "stop":
        return t("actionDetail.outcome.stop", "Stopped");
      case "complete":
        return t("actionDetail.outcome.complete", "Completed");
    }
  }

  const footer = active ? (
    <div className={s.footButtons}>
      <Button variant="secondary" onClick={onChange}>
        {t("actionDetail.change", "Change")}
      </Button>
      <Button onClick={onCheckIn}>{t("actionDetail.recordCheckIn", "Record check-in")}</Button>
    </div>
  ) : null;

  return (
    <ActionDrawerShell container={container} title={action.title} onClose={onClose} footer={footer}>
      <section className={s.card} aria-label={t("actionDetail.cardLabel", "Action")}>
        <div className={s.cardHead}>
          <span className={s.tile}>
            <Icon aria-hidden size={18} />
          </span>
          <div className={s.cardTitleBlock}>
            <span className={s.cardTitle}>{action.title}</span>
            <span className={s.meta}>
              {t("actionDetail.author", {
                name: action.authorName,
                title: titleLabel(action.authorTitle),
                date: formatTimestampDay(action.createdAt),
                defaultValue: "{{name}}, {{title}} · since {{date}}",
              })}
              {previous
                ? ` ${t("actionDetail.changedFrom", { title: previous.title, defaultValue: "(changed from {{title}})" })}`
                : ""}
            </span>
          </div>
        </div>
        <dl className={s.facts}>
          <dt>{t("actionDetail.kind", "Kind")}</dt>
          <dd>{kindLabel(action.kind)}</dd>
          {action.instructions ? (
            <>
              <dt>{t("actionDetail.instructions", "Instructions")}</dt>
              <dd className={s.instructions}>{action.instructions}</dd>
            </>
          ) : null}
          <dt>{t("actionDetail.runs", "Runs")}</dt>
          <dd>
            {action.runsForWeeks === null
              ? runsLabel(null)
              : t("actionDetail.runsFrom", {
                  runs: runsLabel(action.runsForWeeks),
                  date: formatTimestampDay(action.createdAt),
                  defaultValue: "{{runs}} from {{date}}",
                })}
          </dd>
          <dt>{t("actionDetail.checkIn", "Check-in")}</dt>
          <dd>
            {due.on && due.channel
              ? t("actionDetail.checkInSet", {
                  date: formatDay(due.on),
                  channel: channelLabel(due.channel),
                  defaultValue: "{{date}} · {{channel}}",
                })
              : t("actionDetail.noCheckIn", "No check-in set")}
          </dd>
          <dt>{t("actionDetail.visibility", "Visibility")}</dt>
          <dd>
            {action.clientVisible
              ? t("actionDetail.shown", { name: clientName, defaultValue: "Shown in {{name}}'s app" })
              : t("actionDetail.internal", "Internal only")}
          </dd>
        </dl>
        {action.sourceMessageId ? (
          <button type="button" className={s.sourceLink} onClick={() => onOpenMessage(action.sourceMessageId!)}>
            {t("actionDetail.fromMessage", "From the chat message")}
          </button>
        ) : null}
      </section>

      <section aria-labelledby="action-detail-history">
        <h3 id="action-detail-history" className={s.historyHead}>
          {t("actionDetail.history", "History")}
        </h3>
        <ol className={s.history}>
          {history.map((item) => (
            <li key={`${item.type}-${item.type === "version" ? item.action.id : item.checkIn.id}`} className={s.item}>
              <span className={s.itemTitle}>
                {item.type === "version"
                  ? item.previous
                    ? t("actionDetail.itemChanged", {
                        from: item.previous.title,
                        to: item.action.title,
                        defaultValue: "Changed: {{from}} → {{to}}",
                      })
                    : t("actionDetail.itemStarted", { title: item.action.title, defaultValue: "Started: {{title}}" })
                  : outcomeLabel(item.checkIn.outcome)}
                <span className={s.itemDate}> · {formatTimestampDay(item.at)}</span>
              </span>
              <span className={s.meta}>
                {item.type === "version"
                  ? `${item.action.authorName}, ${titleLabel(item.action.authorTitle)}`
                  : `${item.checkIn.authorName}, ${titleLabel(item.checkIn.authorTitle)}`}
                {item.type === "checkIn" && item.checkIn.nextCheckInOn
                  ? ` · ${t("actionDetail.nextCheckIn", {
                      date: formatDay(item.checkIn.nextCheckInOn),
                      defaultValue: "next check-in {{date}}",
                    })}`
                  : ""}
              </span>
              {item.type === "version" && item.action.changeReason ? (
                <span className={s.note}>{item.action.changeReason}</span>
              ) : null}
              {item.type === "checkIn" && item.checkIn.note ? (
                <span className={s.note}>{item.checkIn.note}</span>
              ) : null}
            </li>
          ))}
        </ol>
      </section>
    </ActionDrawerShell>
  );
}
